/* CodeMirror 6 JSON editor core — App Studio flavor of the proven
 * builderOpcode/jsonMode/EditorPanel pattern (same packages, no new deps).
 * Fills its container; the editor IS the scroll surface (no double scroll). */
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef
} from 'react';
import {
    Decoration,
    EditorView,
    highlightActiveLine,
    lineNumbers
} from '@codemirror/view';
import {
    EditorState,
    StateEffect,
    StateField
} from '@codemirror/state';
import {
    json
} from '@codemirror/lang-json';
import {
    highlightSelectionMatches
} from '@codemirror/search';
import {
    codeFolding,
    foldGutter
} from '@codemirror/language';

const setErrorLineEffect = StateEffect.define<number | null>();

const errorLineField = StateField.define({
    create() {
        return Decoration.none;
    },
    update(deco, tr) {
        deco = deco.map(tr.changes);
        for (const e of tr.effects) {
            if (e.is(setErrorLineEffect)) {
                if (e.value == null) return Decoration.none;
                const lineNo = Math.max(1, Math.min(e.value, tr.state.doc.lines));
                const line = tr.state.doc.line(lineNo);
                return Decoration.set([
                    Decoration.line({
                        class: 'cm-aps-errorLine'
                    }).range(line.from)
                ]);
            }
        }
        return deco;
    },
    provide: (f) => EditorView.decorations.from(f)
});

/* A SECOND, transient highlight — the brief flash when the user clicks a
 * validator-error row to jump to its location. Independent of the parse
 * error line above so the two never clobber each other. */
const setFlashLineEffect = StateEffect.define<number | null>();

const flashLineField = StateField.define({
    create() {
        return Decoration.none;
    },
    update(deco, tr) {
        deco = deco.map(tr.changes);
        for (const e of tr.effects) {
            if (e.is(setFlashLineEffect)) {
                if (e.value == null) return Decoration.none;
                const lineNo = Math.max(1, Math.min(e.value, tr.state.doc.lines));
                const line = tr.state.doc.line(lineNo);
                return Decoration.set([
                    Decoration.line({
                        class: 'cm-aps-flashLine'
                    }).range(line.from)
                ]);
            }
        }
        return deco;
    },
    provide: (f) => EditorView.decorations.from(f)
});

/** First 1-based line whose text contains `token` (plain substring, not
 * regex — tokens come from arbitrary validator paths). null if absent. */
function findTokenLine(docText: string, token: string): number | null {
    if (!token) return null;
    const lines = docText.split('\n');
    // Prefer a quoted key/value hit ("token") so we land on the real field
    // rather than an incidental substring; fall back to a raw substring.
    const quoted = '"' + token + '"';
    for (let i = 0; i < lines.length; i++) {
        if (lines[i].includes(quoted)) return i + 1;
    }
    for (let i = 0; i < lines.length; i++) {
        if (lines[i].includes(token)) return i + 1;
    }
    return null;
}

const apsTheme = EditorView.theme({
    '&': {
        height: '100%',
        fontSize: '13px',
        fontFamily: 'var(--aps-font-mono)',
        backgroundColor: 'var(--aps-editor-bg)',
        color: 'var(--aps-text)'
    },
    '.cm-content': {
        padding: '12px 0',
        caretColor: 'var(--aps-primary)'
    },
    '.cm-scroller': {
        overflow: 'auto',
        fontFamily: 'var(--aps-font-mono)',
        lineHeight: '1.6'
    },
    '.cm-gutters': {
        backgroundColor: 'var(--aps-editor-gutter)',
        color: 'var(--aps-text-subtle)',
        border: 'none'
    },
    '.cm-activeLine': {
        backgroundColor: 'var(--aps-editor-line)'
    },
    '.cm-activeLineGutter': {
        backgroundColor: 'var(--aps-editor-line)'
    },
    '.cm-aps-errorLine': {
        backgroundColor: 'var(--aps-editor-error)'
    },
    '.cm-aps-flashLine': {
        backgroundColor: 'var(--aps-primary-soft-strong)',
        boxShadow: 'inset 3px 0 0 0 var(--aps-primary)',
        transition: 'background-color 0.4s ease'
    },
    '&.cm-focused': {
        outline: 'none'
    },
    '.cm-selectionBackground, &.cm-focused .cm-selectionBackground': {
        backgroundColor: 'var(--aps-primary-soft-strong)'
    }
});

export type CodeMirrorJsonHandle = {
    highlightErrorAt: (lineNumber: number) => void;
    clearErrorHighlight: () => void;
    /** Jump to the line of the FIRST resolvable token: scroll it into view and
     * flash it briefly. Accepts one token or an ordered list (tried in order,
     * most-specific first). Returns true if any token was found. */
    revealToken: (token: string | string[]) => boolean;
};

type Props = {
    value: string;
    onChange?: (value: string) => void;
    readOnly?: boolean;
};

const CodeMirrorJson = forwardRef<CodeMirrorJsonHandle, Props>(({
    value,
    onChange,
    readOnly = false
}, ref) => {
    const hostRef = useRef<HTMLDivElement>(null);
    const viewRef = useRef<EditorView | null>(null);
    const settingRef = useRef(false);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useImperativeHandle(ref, () => ({
        highlightErrorAt: (lineNumber: number) => {
            const view = viewRef.current;
            if (!view) return;
            const lineNo = Math.max(1, Math.min(lineNumber, view.state.doc.lines));
            const line = view.state.doc.line(lineNo);
            view.dispatch({
                effects: [
                    setErrorLineEffect.of(lineNumber),
                    EditorView.scrollIntoView(line.from, {
                        y: 'center'
                    })
                ]
            });
        },
        clearErrorHighlight: () => {
            viewRef.current?.dispatch({
                effects: setErrorLineEffect.of(null)
            });
        },
        revealToken: (token: string | string[]): boolean => {
            const view = viewRef.current;
            if (!view) return false;
            const docText = view.state.doc.toString();
            // Try each token in order (most-specific first) and reveal the
            // FIRST one found, so we land on the exact offending line.
            const tokens = Array.isArray(token) ? token : [token];
            let lineNo: number | null = null;
            for (const t of tokens) {
                lineNo = findTokenLine(docText, t);
                if (lineNo != null) break;
            }
            if (lineNo == null) return false;
            const line = view.state.doc.line(lineNo);
            view.dispatch({
                selection: {
                    anchor: line.from
                },
                effects: [
                    setFlashLineEffect.of(lineNo),
                    EditorView.scrollIntoView(line.from, {
                        y: 'center'
                    })
                ]
            });
            view.focus();
            if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
            flashTimerRef.current = setTimeout(() => {
                viewRef.current?.dispatch({
                    effects: setFlashLineEffect.of(null)
                });
                flashTimerRef.current = null;
            }, 1400);
            return true;
        }
    }), []);

    // Clear any pending flash timer on unmount.
    useEffect(() => () => {
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    }, []);

    useEffect(() => {
        if (!hostRef.current) return;
        const startState = EditorState.create({
            doc: value,
            extensions: [
                lineNumbers(),
                foldGutter(),
                codeFolding(),
                highlightActiveLine(),
                highlightSelectionMatches(),
                json(),
                errorLineField,
                flashLineField,
                apsTheme,
                EditorView.lineWrapping,
                EditorState.readOnly.of(readOnly),
                EditorView.updateListener.of((update) => {
                    if (update.docChanged && !settingRef.current) {
                        onChangeRef.current?.(update.state.doc.toString());
                    }
                })
            ]
        });
        const view = new EditorView({
            state: startState,
            parent: hostRef.current
        });
        viewRef.current = view;
        return () => {
            view.destroy();
            viewRef.current = null;
        };
        // Recreate only when read-only mode flips; doc sync is handled below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [readOnly]);

    useEffect(() => {
        const view = viewRef.current;
        if (view && value !== view.state.doc.toString()) {
            settingRef.current = true;
            view.dispatch({
                changes: {
                    from: 0,
                    to: view.state.doc.length,
                    insert: value
                }
            });
            settingRef.current = false;
        }
    }, [value]);

    return <div ref={hostRef} style={{
        height: '100%'
    }} />;
});

CodeMirrorJson.displayName = 'CodeMirrorJson';

export default CodeMirrorJson;
